import { mat4, quat, vec3 } from 'gl-matrix';

import { Scene } from './Scene';
import { TiledObjectModelRef, TiledObjectsRef } from './SceneManifest';
import { ParsedModel } from './loaders/ModelLoader';
import {
  ModelLodKit,
  applyModelLodKitToGroup,
  ensureKitBillboard,
} from './loaders/ModelLodKit';
import { TileArchiveCache } from '../navigation-mesh/TileArchive';
import { inflateGzip } from '../util/gzip';
import { GltfMesh, Mesh } from '../mesh';
import { Material } from '../material';
import { RenderGroup } from '../object/RenderGroup';
import { chebyshevRing } from './util/tile-ring';
import { Light, PointLight, SpotLight } from '../light';
import { CloudVolume } from './cloud-volume/CloudVolume';
import { LiquidSurface } from '../object/primitives/LiquidSurface';
import { Flame } from '../object/primitives/Flame';
import { buildFlameFromRef } from './loaders/FlameLoader';
import {
  tessellateLiquidPolygon,
  PolygonVertex,
} from '../object/primitives/liquid-polygon-tessellator';
import { CompositeInstance, instantiateComposite } from '../composite';
import { SoundEmitterRuntime } from '../audio/SoundEmitterRuntime';

/**
 * Camera-driven streamer for the per-tile OBJECTS chunk inside `.tile` archives.
 * Maintains a 5×5 chebyshev ring around the camera tile (compromise between the
 * navmesh's 3×3 ring and terrain LOD0's 9×9 — far enough that objects don't pop
 * in inside the navigable ring, close enough that startup payload stays bounded).
 *
 * **RenderGroup batching** — entries are bucketed per tile by (model, meshName,
 * primitiveIdx) and instantiated as one {@link RenderGroup} per bucket. The
 * group registers with `ObjectManager` (one octree entry per cluster) and with
 * `InstanceManager` (which routes to the global `(mesh, material)` instance
 * group, preserving today's batching). At 38K visible scatter, this drops the
 * octree visible-set from 38K → ~50 clusters and skips the per-instance hash
 * lookup + matrix-copy work that dominated `prepareFrame`.
 *
 * **Overlap copies** — the publisher writes byte-identical entries into every
 * tile whose payload-AABB the entry overlaps. We do NOT dedup by entry name:
 * each tile gets its own RenderGroup containing exactly its payload's entries.
 * If two ring-adjacent tiles both contain the same overlap-copy entry, that
 * entry renders twice with identical matrices — opaque depth test makes this
 * invisible (no z-fighting because the depth values are bit-equal) and the perf
 * cost is a small fraction of total instances. Dropping dedup is the unlock for
 * RG-batching: per-entry refcounting would require destroying + rebuilding
 * RenderGroups whenever ANY tile they touch evicts, which destroys the win.
 *
 * Meshes are cached across tiles by `(modelId, meshName, primitiveIdx)` so two
 * tiles that load the same tree share a single GL VAO. Meshes are ref-counted
 * per tile: a mesh is destroyed when the last tile that references it evicts.
 */
export class TiledObjectStreamer {
  private static readonly RING_RADIUS = 2;

  private readonly tilesX: number;
  private readonly tilesZ: number;
  private readonly tileWorldSize: number;
  private readonly worldMinX: number;
  private readonly worldMinZ: number;
  private readonly objectTileSet: Set<string>;
  private readonly lightTileSet: Set<string>;
  private readonly effectTileSet: Set<string>;
  private readonly compositeTileSet: Set<string>;
  private readonly soundEmitterTileSet: Set<string>;
  /** Union of object/light/effect/composite/emitter tile sets — any tile that carries at least one chunk we care about. */
  private readonly streamedTileSet: Set<string>;

  /** Loaded tile state — owns the RenderGroups created for that tile. */
  private loadedTiles = new Map<string, LoadedTile>();
  private inflight = new Map<string, Promise<void>>();
  /** Shared mesh cache across tile loads to maximize InstanceManager grouping. */
  private meshCache = new Map<string, GltfMesh>();
  /** Per-cache-key ref count — mesh destroyed when count reaches 0 on tile eviction. */
  private meshRefCounts = new Map<string, number>();

  // ── Light streaming state ─────────────────────────────────────────────
  /** lightId → created Light; created on first tile load, destroyed on last unload. */
  private activeLights = new Map<number, Light>();
  /** lightId → ref count across loaded tiles (overlap copies share the same Light). */
  private lightRefCounts = new Map<number, number>();
  /** tile key → stable lightId[] parsed from that tile's LIGHTS chunk. */
  private tileLightIds = new Map<string, number[]>();

  // ── Effect streaming state ────────────────────────────────────────────
  /** effectId → ref count; effect is alive while count > 0. */
  private effectRefCounts = new Map<string, number>();
  /** effectId → LiquidSurface or CloudVolume instance. */
  private activeEffects = new Map<string, LiquidSurface | CloudVolume | Flame>();
  /** tile key → effectId[] parsed from that tile's EFFECTS chunk. */
  private tileEffectIds = new Map<string, string[]>();

  // ── Composite streaming state ─────────────────────────────────────────
  /** instanceId → ref count. Composites have no overlap copies today, so the
   *  count is always 0 or 1 — but the ref-count plumbing matches lights/effects
   *  in case a future composite type spans multiple tiles. */
  private compositeRefCounts = new Map<string, number>();
  /** instanceId → live CompositeInstance handle. */
  private activeComposites = new Map<string, CompositeInstance>();
  /** tile key → instanceId[] parsed from that tile's COMPOSITES chunk. */
  private tileCompositeIds = new Map<string, string[]>();

  // ── Sound emitter streaming state ─────────────────────────────────────
  /** emitterId → ref count; emitter alive while count > 0. */
  private soundEmitterRefCounts = new Map<string, number>();
  /** tile key → emitter ids parsed from that tile's SOUND_EMITTERS chunk. */
  private tileSoundEmitterIds = new Map<string, string[]>();
  /** emitterId → clipId; used at evict so library.release pairs with library.acquire even if scene.soundEmitters was cleared mid-teardown. */
  private soundEmitterClipIds = new Map<string, string>();
  /**
   * Far-billboard RenderGroups keyed by primary tile coord. Each tile's
   * billboards are added to objectManager only when that tile is OUTSIDE the
   * streamer's near ring; once the ring covers the tile, they are removed so
   * the kit's billboard tier on the near group renders the same instances
   * without doubling up.
   *
   * One RG per (tile, model) — the bundle's per-tile entries are bucketed by
   * modelRefId and then collapsed into one cluster per bucket. At altitude
   * with most tiles outside the near ring, this is the dominant population.
   */
  private farBillboardsByTile = new Map<string, RenderGroup[]>();
  /** Tracks whether each tile's far billboards are currently registered with the scene. */
  private farTileActive = new Set<string>();
  /**
   * Per-RG flag: true when the RG was built from a billboard tier (so its host
   * IG should adopt billboard-specific overrides: `castsShadows=false`,
   * `baseDoubleSided=true`). Far-include placements using a decimated tier as
   * their far-LOD are stored with `false` so we don't break their shadows.
   */
  private farRgIsBillboard = new WeakMap<RenderGroup, boolean>();

  private cameraTx = Number.NaN;
  private cameraTz = Number.NaN;

  constructor(
    private readonly scene: Scene,
    private readonly ref: TiledObjectsRef,
    private readonly modelCache: Map<string, ParsedModel>,
    private readonly archiveCache: TileArchiveCache,
  ) {
    this.tilesX = ref.tilesX;
    this.tilesZ = ref.tilesZ;
    this.tileWorldSize = ref.tileWorldSize;
    this.worldMinX = ref.worldMinX;
    this.worldMinZ = ref.worldMinZ;
    this.objectTileSet = new Set((ref.objectTiles ?? []).map(([tx, tz]) => `${tx},${tz}`));
    this.lightTileSet = new Set((ref.lightTiles ?? []).map(([tx, tz]) => `${tx},${tz}`));
    this.effectTileSet = new Set((ref.effectTiles ?? []).map(([tx, tz]) => `${tx},${tz}`));
    this.compositeTileSet = new Set(
      (ref.compositeTiles ?? []).map(([tx, tz]) => `${tx},${tz}`),
    );
    this.soundEmitterTileSet = new Set(
      (ref.soundEmitterTiles ?? []).map(([tx, tz]) => `${tx},${tz}`),
    );
    this.streamedTileSet = new Set([
      ...this.objectTileSet,
      ...this.lightTileSet,
      ...this.effectTileSet,
      ...this.compositeTileSet,
      ...this.soundEmitterTileSet,
    ]);
  }

  /**
   * Block on loading the inner 3×3 around the spawn point so spawn-rendering is
   * deterministic, then fire-and-forget the rest of the 5×5 ring in the background.
   */
  async loadInitialTiles(spawnX: number, spawnZ: number): Promise<void> {
    const tx = Math.floor((spawnX - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((spawnZ - this.worldMinZ) / this.tileWorldSize);
    this.cameraTx = tx;
    this.cameraTz = tz;

    const inner: Promise<void>[] = [];
    const outer: Promise<void>[] = [];
    for (const key of this.ringKeys(tx, tz, TiledObjectStreamer.RING_RADIUS)) {
      const [ktxStr, ktzStr] = key.split(',');
      const ktx = parseInt(ktxStr);
      const ktz = parseInt(ktzStr);
      const chebyshev = Math.max(Math.abs(ktx - tx), Math.abs(ktz - tz));
      const promise = this.requestTile(key);
      if (chebyshev <= 1) inner.push(promise);
      else outer.push(promise);
    }
    await Promise.all(inner);
    void Promise.all(outer);
  }

  updateCamera(x: number, z: number): void {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    if (tx === this.cameraTx && tz === this.cameraTz) return;
    this.cameraTx = tx;
    this.cameraTz = tz;

    const needed = this.ringKeys(tx, tz, TiledObjectStreamer.RING_RADIUS);

    // Evict tiles outside the ring
    for (const key of [...this.loadedTiles.keys()]) {
      if (!needed.has(key)) this.unloadTile(key);
    }
    // Request missing tiles
    for (const key of needed) {
      if (!this.loadedTiles.has(key) && !this.inflight.has(key)) {
        void this.requestTile(key);
      }
    }
  }

  /**
   * Load the whole-world far-objects bundle and bucket each tile's entries
   * by model into one RenderGroup per (tile, model). Tiles already inside the
   * streamer's near ring (loaded by `loadInitialTiles`) stay on the sidelines;
   * everything else is registered with the scene immediately.
   *
   * Subsequent ring transitions in [loadTile] / [unloadTile] toggle each
   * tile's billboard RenderGroups in/out of the scene so we never
   * double-render with the kit's billboard tier on near groups.
   */
  async loadFarObjects(path: string): Promise<void> {
    let data: Uint8Array;
    try {
      try {
        const compressed = await this.scene.ctx.resourceLoader.loadResource(path + '.gz');
        data = await inflateGzip(compressed);
      } catch {
        data = await this.scene.ctx.resourceLoader.loadResource(path);
      }
    } catch (e) {
      console.warn(`Failed to fetch far-objects bundle '${path}':`, e);
      return;
    }
    let bundle: FarObjectsBundle;
    try {
      bundle = parseFarObjectsBundle(data);
    } catch (e) {
      console.warn(`Failed to parse far-objects bundle '${path}':`, e);
      return;
    }

    // Group instances by primary tile coord (same formula the publisher uses).
    const grouped = new Map<string, FarObjectEntry[]>();
    for (const entry of bundle.entries) {
      const tx = Math.floor((entry.position[0] - this.worldMinX) / this.tileWorldSize);
      const tz = Math.floor((entry.position[2] - this.worldMinZ) / this.tileWorldSize);
      if (tx < 0 || tx >= this.tilesX || tz < 0 || tz >= this.tilesZ) continue;
      const key = `${tx},${tz}`;
      let list = grouped.get(key);
      if (!list) {
        list = [];
        grouped.set(key, list);
      }
      list.push(entry);
    }

    let totalInstantiated = 0;
    for (const [tileKey, tileEntries] of grouped) {
      const rgs = this.buildFarBillboardGroups(tileKey, tileEntries, bundle.models);
      if (rgs.length === 0) continue;
      this.farBillboardsByTile.set(tileKey, rgs);
      // If this tile is NOT already inside the near ring, light up its
      // billboards. Tiles in the ring stay dormant until they leave it.
      if (!this.loadedTiles.has(tileKey)) {
        this.activateFarTile(tileKey);
      }
      for (const rg of rgs) totalInstantiated += rg.instanceCount;
    }
    if (totalInstantiated > 0) {
      console.info(
        `[far-objects] instantiated ${totalInstantiated} billboards across ${this.farBillboardsByTile.size} tiles (${[...this.farBillboardsByTile.values()].reduce((s, l) => s + l.length, 0)} render groups)`,
      );
    }
  }

  /** Tear down all loaded objects, lights, effects, far billboards, and shared meshes; called on scene detach. */
  destroy(): void {
    for (const key of [...this.loadedTiles.keys()]) this.unloadTile(key);
    // Far billboards aren't in `loadedTiles`; deactivate any that are still in
    // the scene, then drop all references so the GltfMesh destruction below
    // doesn't tear out a mesh that's still being referenced by an instance.
    for (const key of [...this.farTileActive]) this.deactivateFarTile(key);
    this.farBillboardsByTile.clear();
    const gl = this.scene.ctx.gl;
    // Clean up any lights that weren't removed via tile unload (shouldn't happen, but be safe).
    for (const [, light] of this.activeLights) {
      if (light.shadowMap?.glTexture) gl.deleteTexture(light.shadowMap.glTexture);
      this.scene.lightManager.removeLight(light);
    }
    this.activeLights.clear();
    // Clean up any effects that weren't removed via tile unload.
    for (const [, effect] of this.activeEffects) this.destroyEffect(effect);
    this.activeEffects.clear();
    // Composites: any instance that survived to here is leaking its child mesh +
    // light (and any shadow texture on the light). `instance.destroy(scene)`
    // routes both back through their managers.
    for (const [instanceId, instance] of this.activeComposites) {
      instance.destroy(this.scene);
      this.scene.compositeInstances.delete(instanceId);
    }
    this.activeComposites.clear();
    this.compositeRefCounts.clear();
    this.tileCompositeIds.clear();
    for (const mesh of this.meshCache.values()) mesh.destroy(gl);
    this.meshCache.clear();
    this.meshRefCounts.clear();
  }

  /**
   * Build one RenderGroup per (tile, model) bucket of far-billboard entries.
   * Each model's `ModelLodKit` provides the billboard mesh + impostor material
   * (lazy-built from a temporary sample object if the near streamer hasn't
   * touched the kit yet).
   *
   * Far billboards are NOT registered with the scene until `activateFarTile`
   * promotes them when the tile leaves the near ring; the RGs returned here
   * live in `farBillboardsByTile` until then.
   */
  private destroyEffect(effect: LiquidSurface | CloudVolume | Flame): void {
    if (effect instanceof LiquidSurface) {
      this.scene.objectManager.removeObject(effect);
      effect.destroy();
    } else if (effect instanceof CloudVolume) {
      const idx = this.scene.cloudVolumes.indexOf(effect);
      if (idx >= 0) this.scene.cloudVolumes.splice(idx, 1);
    } else {
      const idx = this.scene.flames.indexOf(effect);
      if (idx >= 0) this.scene.flames.splice(idx, 1);
    }
  }

  private buildFarBillboardGroups(
    tileKey: string,
    entries: FarObjectEntry[],
    models: TiledObjectModelRef[],
  ): RenderGroup[] {
    // Bucket by (modelRefId, forceFlag). Flagged and unflagged placements of
    // the same model need separate RGs: flagged buckets bypass the
    // skipConfig/no-billboard guard and may render via a decimated tier rather
    // than a billboard.
    type Bucket = { modelRefId: number; forced: boolean; entries: FarObjectEntry[] };
    const buckets = new Map<string, Bucket>();
    for (const e of entries) {
      const forced = (e.flags & FAR_FLAG_FORCE_INCLUDE) !== 0;
      const key = `${e.modelRefId}:${forced ? 1 : 0}`;
      let b = buckets.get(key);
      if (!b) {
        b = { modelRefId: e.modelRefId, forced, entries: [] };
        buckets.set(key, b);
      }
      b.entries.push(e);
    }

    const out: RenderGroup[] = [];
    for (const bucket of buckets.values()) {
      const ref = models[bucket.modelRefId];
      if (!ref) continue;
      const model = this.modelCache.get(ref.model);
      if (!model) continue;
      const kit = this.scene.modelLodKits.get(ref.model);

      // Pick the far-render mesh + material for this bucket.
      //  - Unflagged bucket: existing behavior — billboard required, no skipConfig.
      //  - Flagged bucket: lowest non-Skip LOD — billboard if present, else
      //    coarsest decimated tier, else full mesh fallback. The Skip cull
      //    is bypassed regardless of kit.skipConfig.
      let mesh: Mesh | undefined;
      let material: Material | undefined;
      let isBillboard = false;

      if (!bucket.forced) {
        if (!kit?.billboardConfig || kit.skipConfig) continue;
        if (!ensureFarBillboard(this.scene, kit, ref, model)) continue;
        mesh = kit.billboardMesh!;
        material = kit.billboardMaterial!;
        isBillboard = true;
      } else {
        // Flagged: prefer billboard if available (cheaper).
        if (kit?.billboardConfig) {
          if (!ensureFarBillboard(this.scene, kit, ref, model)) continue;
          mesh = kit.billboardMesh!;
          material = kit.billboardMaterial!;
          isBillboard = true;
        } else if (kit && kit.decimatedTiers.length > 0) {
          // Highest-maxDistance decimated tier = lowest detail short of Skip.
          let coarsest = kit.decimatedTiers[0];
          for (const tier of kit.decimatedTiers) {
            if (tier.maxDistance > coarsest.maxDistance) coarsest = tier;
          }
          mesh = coarsest.mesh;
          if (coarsest.materialIndex !== undefined) {
            material = model.materials.get(coarsest.materialIndex);
          }
        } else {
          // No LOD kit at all — fall back to the full source mesh.
          const resolved =
            ref.meshName === '*' ? model.meshes[0] : model.meshByName.get(ref.meshName);
          const prim = resolved?.primitives[0];
          if (!prim) continue;
          const cacheKey = `${ref.model}:${resolved!.name}:0`;
          let cached = this.meshCache.get(cacheKey);
          if (!cached) {
            cached = new GltfMesh(cacheKey, this.scene.ctx, this.scene, prim);
            this.meshCache.set(cacheKey, cached);
          }
          // Pin the mesh: incremented once at bundle load, never decremented
          // (no `unloadTile` for far entries). Near tiles can still pair their
          // own +1/-1 against the same key, so this just adds a permanent
          // baseline that keeps the mesh alive for the bundle's lifetime.
          // `destroy()` iterates `meshCache.values()` directly so cleanup is
          // unaffected.
          this.meshRefCounts.set(cacheKey, (this.meshRefCounts.get(cacheKey) ?? 0) + 1);
          mesh = cached;
          if (prim.materialIndex !== undefined) {
            material = model.materials.get(prim.materialIndex);
          }
        }
      }

      if (!mesh) continue;

      // Pre-bake matrices and per-instance bounding spheres for this bucket.
      const count = bucket.entries.length;
      const matrices = new Float32Array(count * 16);
      const spheres = new Float32Array(count * 4);
      const tmpMat = mat4.create();
      const tmpQuat = quat.create();
      const tmpScale = vec3.create();
      const localCenter = mesh.localBounds.boundingSphere.position;
      const localRadius = mesh.localBounds.boundingSphere.radius;
      const worldCenter = vec3.create();
      for (let i = 0; i < count; i++) {
        const e = bucket.entries[i];
        quat.set(tmpQuat, e.rotation[0], e.rotation[1], e.rotation[2], e.rotation[3]);
        tmpScale[0] = tmpScale[1] = tmpScale[2] = e.scale;
        mat4.fromRotationTranslationScale(tmpMat, tmpQuat, e.position as vec3, tmpScale);
        matrices.set(tmpMat, i * 16);
        vec3.transformMat4(worldCenter, localCenter, tmpMat);
        const o = i * 4;
        spheres[o] = worldCenter[0];
        spheres[o + 1] = worldCenter[1];
        spheres[o + 2] = worldCenter[2];
        spheres[o + 3] = localRadius * e.scale;
      }

      const rgKey = `${tileKey}:far${bucket.forced ? '!' : ''}:${ref.model}`;
      const rg = new RenderGroup(rgKey, mesh, material, matrices, spheres);
      this.farRgIsBillboard.set(rg, isBillboard);
      out.push(rg);
    }
    return out;
  }

  private activateFarTile(tileKey: string): void {
    if (this.farTileActive.has(tileKey)) return;
    const rgs = this.farBillboardsByTile.get(tileKey);
    if (!rgs || rgs.length === 0) return;
    this.scene.objectManager.addObjects(rgs);
    for (const rg of rgs) {
      this.scene.instanceManager.registerGroup(rg);
      // Billboard far RGs: base mesh is the cross-billboard — exclude from
      // depth/shadow passes (cardboard shadows) and render double-sided.
      // Far-include decimated/full-mesh RGs are real geometry — leave their
      // shadow/culling at the IG default so the landmark looks correct.
      if (rg.targetInstanceGroup && this.farRgIsBillboard.get(rg)) {
        rg.targetInstanceGroup.castsShadows = false;
        rg.targetInstanceGroup.baseDoubleSided = true;
      }
    }
    this.farTileActive.add(tileKey);
  }

  private deactivateFarTile(tileKey: string): void {
    // Normal when the tile was already in the near ring when far-objects loaded.
    if (!this.farTileActive.has(tileKey)) return;
    const rgs = this.farBillboardsByTile.get(tileKey);
    if (!rgs) return;
    for (const rg of rgs) {
      this.scene.instanceManager.unregisterGroup(rg);
      this.scene.objectManager.removeObject(rg);
    }
    this.farTileActive.delete(tileKey);
  }

  /**
   * Build the chebyshev ring of tile keys centered on (cx, cz), clipped to the
   * tile-grid bounds and to tiles known to actually carry an OBJECTS chunk.
   */
  private ringKeys(cx: number, cz: number, radius: number): Set<string> {
    const ring = chebyshevRing(cx, cz, radius, this.tilesX, this.tilesZ);
    const result = new Set<string>();
    for (const key of ring) if (this.streamedTileSet.has(key)) result.add(key);
    return result;
  }

  private requestTile(key: string): Promise<void> {
    const existing = this.inflight.get(key);
    if (existing) return existing;
    const promise = this.loadTile(key).finally(() => this.inflight.delete(key));
    this.inflight.set(key, promise);
    return promise;
  }

  private async loadTile(key: string): Promise<void> {
    if (this.loadedTiles.has(key)) return;
    const [txStr, tzStr] = key.split(',');
    const tx = parseInt(txStr);
    const tz = parseInt(tzStr);
    const archive = await this.archiveCache.get(tx, tz);
    if (!archive) return;

    // ── OBJECTS ──────────────────────────────────────────────────────────
    const renderGroups: RenderGroup[] = [];
    const usedMeshKeys = new Set<string>();
    if (archive.objects && this.objectTileSet.has(key)) {
      let group: TiledObjectGroup;
      try {
        group = parseObjectsPayload(archive.objects);
      } catch (e) {
        console.warn(`Failed to parse OBJECTS chunk for tile (${tx},${tz}):`, e);
        group = { models: [], entries: [] };
      }
      const bucketed = new Map<number, TiledObjectEntry[]>();
      for (const entry of group.entries) {
        let list = bucketed.get(entry.modelRefId);
        if (!list) {
          list = [];
          bucketed.set(entry.modelRefId, list);
        }
        list.push(entry);
      }
      for (const [modelRefId, entries] of bucketed) {
        const built = this.buildBucketGroups(key, modelRefId, entries, group.models, usedMeshKeys);
        for (const rg of built) renderGroups.push(rg);
      }
      for (const mk of usedMeshKeys)
        this.meshRefCounts.set(mk, (this.meshRefCounts.get(mk) ?? 0) + 1);
    }

    // ── LIGHTS ───────────────────────────────────────────────────────────
    if (archive.lights && this.lightTileSet.has(key)) {
      let lightEntries: TileLightEntry[];
      try {
        lightEntries = parseLightsPayload(archive.lights);
      } catch (e) {
        console.warn(`Failed to parse LIGHTS chunk for tile (${tx},${tz}):`, e);
        lightEntries = [];
      }
      const lightIds: number[] = [];
      for (const entry of lightEntries) {
        const prev = this.lightRefCounts.get(entry.lightId) ?? 0;
        this.lightRefCounts.set(entry.lightId, prev + 1);
        lightIds.push(entry.lightId);
        if (prev === 0) {
          const light = createLightFromEntry(entry);
          this.activeLights.set(entry.lightId, light);
          this.scene.lightManager.addLight(light);
        }
      }
      if (lightIds.length > 0) this.tileLightIds.set(key, lightIds);
    }

    // ── EFFECTS ──────────────────────────────────────────────────────────
    if (archive.effects && this.effectTileSet.has(key)) {
      let effectEntries: TileEffectEntry[];
      try {
        effectEntries = parseEffectsPayload(archive.effects);
      } catch (e) {
        console.warn(`Failed to parse EFFECTS chunk for tile (${tx},${tz}):`, e);
        effectEntries = [];
      }
      const effectIds: string[] = [];
      for (const entry of effectEntries) {
        const prev = this.effectRefCounts.get(entry.id) ?? 0;
        this.effectRefCounts.set(entry.id, prev + 1);
        effectIds.push(entry.id);
        if (prev === 0) {
          if (entry.kind === 'liquid') {
            const surface = createLiquidSurface(this.scene, entry);
            if (surface) {
              this.activeEffects.set(entry.id, surface);
              this.scene.objectManager.addObject(surface);
            }
          } else if (entry.kind === 'cloud') {
            const cloud = new CloudVolume({
              id: entry.id,
              name: entry.name ?? entry.id,
              polygon: entry.polygon.map(([x, z]) => [x, z]),
              baseY: entry.baseY!,
              topY: entry.topY!,
              edgeFeather: entry.edgeFeather,
              density: entry.density,
              coverage: entry.coverage,
              noiseScale: entry.noiseScale,
              windDirX: entry.windDirX,
              windDirZ: entry.windDirZ,
              windSpeed: entry.windSpeed,
            });
            this.activeEffects.set(entry.id, cloud);
            this.scene.cloudVolumes.push(cloud);
          } else {
            // Flame: tile-streamed point primitive. Reuse the same builder the
            // manifest loader uses so the simplified-knob mapping stays
            // consistent.
            const flame = buildFlameFromRef({
              id: entry.id,
              position: entry.position!,
              scale: entry.scale!,
              intensity: entry.flameIntensity!,
              color: entry.flameColor!,
            });
            this.activeEffects.set(entry.id, flame);
            this.scene.flames.push(flame);
          }
        }
      }
      if (effectIds.length > 0) this.tileEffectIds.set(key, effectIds);
    }

    // ── COMPOSITES ───────────────────────────────────────────────────────
    if (archive.composites && this.compositeTileSet.has(key)) {
      let compositeEntries: TileCompositeEntry[];
      try {
        compositeEntries = parseCompositesPayload(archive.composites);
      } catch (e) {
        console.warn(`Failed to parse COMPOSITES chunk for tile (${tx},${tz}):`, e);
        compositeEntries = [];
      }
      const instanceIds: string[] = [];
      for (const entry of compositeEntries) {
        const prev = this.compositeRefCounts.get(entry.instanceId) ?? 0;
        this.compositeRefCounts.set(entry.instanceId, prev + 1);
        instanceIds.push(entry.instanceId);
        if (prev > 0) continue;
        const def = this.scene.compositeDefs.get(entry.compositeId);
        if (!def) {
          // No def in the manifest's `compositeDefs` table — publisher should
          // have caught this. Skip rather than throw so one bad composite
          // doesn't take down the whole tile.
          console.warn(
            `Composite '${entry.instanceId}' references unknown compositeId '${entry.compositeId}'`,
          );
          continue;
        }
        const instance = instantiateComposite(this.scene, def, {
          instanceId: entry.instanceId,
          compositeId: entry.compositeId,
          position: entry.position,
          rotation: entry.rotation,
          scale: entry.scale,
          state: entry.state as Record<string, number | boolean | string> | undefined,
          seed: entry.seed,
        });
        this.activeComposites.set(entry.instanceId, instance);
      }
      if (instanceIds.length > 0) this.tileCompositeIds.set(key, instanceIds);
    }

    // ── SOUND EMITTERS ──────────────────────────────────────────────────
    if (archive.soundEmitters && this.soundEmitterTileSet.has(key)) {
      let emitterEntries: TileSoundEmitterEntry[];
      try {
        emitterEntries = parseSoundEmittersPayload(archive.soundEmitters);
      } catch (e) {
        console.warn(`Failed to parse SOUND_EMITTERS chunk for tile (${tx},${tz}):`, e);
        emitterEntries = [];
      }
      const emitterIds: string[] = [];
      for (const entry of emitterEntries) {
        const prev = this.soundEmitterRefCounts.get(entry.id) ?? 0;
        this.soundEmitterRefCounts.set(entry.id, prev + 1);
        emitterIds.push(entry.id);
        if (prev > 0) continue;
        // Fire-and-forget acquire so the runtime's retry path (bed source) and
        // playOneShot (spawn mode) hit a warm cache shortly after tile load.
        // Symmetrically released in unloadTile so clip refcounts return to zero.
        void this.scene.ctx.engine.audio.library.acquire(entry.clipId).catch(() => {});
        this.soundEmitterClipIds.set(entry.id, entry.clipId);
        const runtime = new SoundEmitterRuntime(this.scene.ctx.engine.audio, {
          id: entry.id,
          position: entry.position,
          clipId: entry.clipId,
          volume: entry.volume,
          spawnIntervalSec: entry.spawnIntervalSec,
        });
        this.scene.soundEmitters.set(entry.id, runtime);
      }
      if (emitterIds.length > 0) this.tileSoundEmitterIds.set(key, emitterIds);
    }

    this.loadedTiles.set(key, { renderGroups, meshKeys: [...usedMeshKeys] });

    // Tile is now in the near ring; the kit's billboard tier on the near group
    // covers far-distance rendering for these instances, so retire the
    // standalone far billboards to avoid double-rendering.
    this.deactivateFarTile(key);
  }

  private unloadTile(key: string): void {
    const tile = this.loadedTiles.get(key);
    if (!tile) return;
    for (const rg of tile.renderGroups) {
      this.scene.instanceManager.unregisterGroup(rg);
      this.scene.objectManager.removeObject(rg);
    }
    this.loadedTiles.delete(key);

    // ── MESH cache eviction ──────────────────────────────────────────────
    // Decrement ref counts for all meshes this tile held. When a count reaches
    // zero, no remaining loaded tile references the mesh — destroy its GL
    // resources. Safe to call after unregisterGroup: the InstanceGroup for
    // (mesh, material) is already empty (or shared with another tile still
    // alive), so the VAO/buffer it owns has already been destroyed or stays
    // valid for the surviving tile.
    const gl = this.scene.ctx.gl;
    for (const mk of tile.meshKeys) {
      const count = (this.meshRefCounts.get(mk) ?? 1) - 1;
      if (count <= 0) {
        this.meshCache.get(mk)?.destroy(gl);
        this.meshCache.delete(mk);
        this.meshRefCounts.delete(mk);
      } else {
        this.meshRefCounts.set(mk, count);
      }
    }

    // Release the raw archive bytes. navmesh evicts archives for the 3×3 ring;
    // tiles at chebyshev distance 2 (objects-only ring) would never be freed
    // without this call.
    const [txStr, tzStr] = key.split(',');
    this.archiveCache.evict(parseInt(txStr), parseInt(tzStr));

    // ── LIGHTS cleanup ───────────────────────────────────────────────────
    const lightIds = this.tileLightIds.get(key);
    if (lightIds) {
      for (const lightId of lightIds) {
        const count = (this.lightRefCounts.get(lightId) ?? 1) - 1;
        if (count <= 0) {
          this.lightRefCounts.delete(lightId);
          const light = this.activeLights.get(lightId);
          if (light) {
            light.shadowMap?.destroy();
            this.scene.lightManager.removeLight(light);
            this.activeLights.delete(lightId);
          }
        } else {
          this.lightRefCounts.set(lightId, count);
        }
      }
      this.tileLightIds.delete(key);
    }

    // ── EFFECTS cleanup ──────────────────────────────────────────────────
    const effectIds = this.tileEffectIds.get(key);
    if (effectIds) {
      for (const effectId of effectIds) {
        const count = (this.effectRefCounts.get(effectId) ?? 1) - 1;
        if (count <= 0) {
          this.effectRefCounts.delete(effectId);
          const effect = this.activeEffects.get(effectId);
          if (effect) {
            this.destroyEffect(effect);
            this.activeEffects.delete(effectId);
          }
        } else {
          this.effectRefCounts.set(effectId, count);
        }
      }
      this.tileEffectIds.delete(key);
    }

    // ── COMPOSITES cleanup ───────────────────────────────────────────────
    const instanceIds = this.tileCompositeIds.get(key);
    if (instanceIds) {
      for (const instanceId of instanceIds) {
        const count = (this.compositeRefCounts.get(instanceId) ?? 1) - 1;
        if (count <= 0) {
          this.compositeRefCounts.delete(instanceId);
          const instance = this.activeComposites.get(instanceId);
          if (instance) {
            instance.destroy(this.scene);
            this.activeComposites.delete(instanceId);
            this.scene.compositeInstances.delete(instanceId);
          }
        } else {
          this.compositeRefCounts.set(instanceId, count);
        }
      }
      this.tileCompositeIds.delete(key);
    }

    // ── SOUND EMITTERS cleanup ──────────────────────────────────────────
    const emitterIds = this.tileSoundEmitterIds.get(key);
    if (emitterIds) {
      const lib = this.scene.ctx.engine.audio.library;
      for (const emitterId of emitterIds) {
        const count = (this.soundEmitterRefCounts.get(emitterId) ?? 1) - 1;
        if (count <= 0) {
          this.soundEmitterRefCounts.delete(emitterId);
          // Pair the load-time `library.acquire` so the clip can drop to zero
          // refs once no tile references it. Tracked in our own map so this
          // works even if scene.soundEmitters was cleared mid-teardown.
          const clipId = this.soundEmitterClipIds.get(emitterId);
          if (clipId !== undefined) {
            lib.release(clipId);
            this.soundEmitterClipIds.delete(emitterId);
          }
          const runtime = this.scene.soundEmitters.get(emitterId);
          if (runtime) {
            runtime.destroy();
            this.scene.soundEmitters.delete(emitterId);
          }
        } else {
          this.soundEmitterRefCounts.set(emitterId, count);
        }
      }
      this.tileSoundEmitterIds.delete(key);
    }

    // Tile left the near ring; bring its standalone far billboards back so the
    // distant view of those objects is preserved.
    this.activateFarTile(key);
  }

  /**
   * Bucket a tile's entries that share a `modelRefId` into one RenderGroup
   * per (mesh, primitive). Walks the model's meshList × primitives once,
   * pre-computes every entry's mat4 + bounding sphere, and hands the typed
   * arrays to the RenderGroup constructor by reference (no copy).
   */
  private buildBucketGroups(
    tileKey: string,
    modelRefId: number,
    entries: TiledObjectEntry[],
    models: TiledObjectModelRef[],
    usedMeshKeys: Set<string>,
  ): RenderGroup[] {
    const ref = models[modelRefId];
    if (!ref) {
      console.warn(
        `OBJECTS chunk referenced unknown modelRefId ${modelRefId} (table size ${models.length})`,
      );
      return [];
    }
    const model = this.modelCache.get(ref.model);
    if (!model) {
      console.warn(`Model '${ref.model}' not found in modelCache`);
      return [];
    }
    const resolved = ref.meshName === '*' ? null : model.meshByName.get(ref.meshName);
    const meshList = resolved ? [resolved] : ref.meshName === '*' ? model.meshes : [];
    if (meshList.length === 0) {
      console.warn(`Mesh '${ref.meshName}' not found in model '${ref.model}'`);
      return [];
    }

    // Pre-bake the matrices once for this bucket. Every (mesh, primitive) in
    // the bucket shares the same per-entry transform; only local bounds differ.
    // Temporaries (mat, quat, scale) are hoisted — the scale-Array literal
    // was being reallocated per entry, churning the GC at dense scatter.
    const count = entries.length;
    const sharedMatrices = new Float32Array(count * 16);
    const tmpMat = mat4.create();
    const tmpQuat = quat.create();
    const tmpScale = vec3.create();
    for (let i = 0; i < count; i++) {
      const e = entries[i];
      quat.set(tmpQuat, e.rotation[0], e.rotation[1], e.rotation[2], e.rotation[3]);
      tmpScale[0] = tmpScale[1] = tmpScale[2] = e.scale;
      mat4.fromRotationTranslationScale(tmpMat, tmpQuat, e.position as vec3, tmpScale);
      sharedMatrices.set(tmpMat, i * 16);
    }

    const out: RenderGroup[] = [];
    for (const r of meshList) {
      for (let j = 0; j < r.primitives.length; j++) {
        const prim = r.primitives[j];
        const cacheKey = `${ref.model}:${r.name}:${j}`;
        usedMeshKeys.add(cacheKey);
        let mesh = this.meshCache.get(cacheKey);
        if (!mesh) {
          mesh = new GltfMesh(cacheKey, this.scene.ctx, this.scene, prim);
          this.meshCache.set(cacheKey, mesh);
        }
        const material: Material | undefined =
          prim.materialIndex !== undefined ? model.materials.get(prim.materialIndex) : undefined;
        const localSphere = mesh.localBounds.boundingSphere;
        const localCenter = localSphere.position;
        const localRadius = localSphere.radius;

        // Per-instance world-space sphere: transform local center, scale radius.
        const spheres = new Float32Array(count * 4);
        const worldCenter = vec3.create();
        for (let i = 0; i < count; i++) {
          const off = i * 16;
          worldCenter[0] =
            sharedMatrices[off + 0] * localCenter[0] +
            sharedMatrices[off + 4] * localCenter[1] +
            sharedMatrices[off + 8] * localCenter[2] +
            sharedMatrices[off + 12];
          worldCenter[1] =
            sharedMatrices[off + 1] * localCenter[0] +
            sharedMatrices[off + 5] * localCenter[1] +
            sharedMatrices[off + 9] * localCenter[2] +
            sharedMatrices[off + 13];
          worldCenter[2] =
            sharedMatrices[off + 2] * localCenter[0] +
            sharedMatrices[off + 6] * localCenter[1] +
            sharedMatrices[off + 10] * localCenter[2] +
            sharedMatrices[off + 14];
          const so = i * 4;
          spheres[so] = worldCenter[0];
          spheres[so + 1] = worldCenter[1];
          spheres[so + 2] = worldCenter[2];
          spheres[so + 3] = localRadius * entries[i].scale;
        }

        // Each RG needs its own matrices typed-array — InstanceGroup uses it
        // as the bufferSubData source and treats it as exclusively-owned.
        // Cloning is a single Float32Array copy (no per-instance loop).
        const rgMatrices = new Float32Array(sharedMatrices);
        const rgKey = `${tileKey}:${cacheKey}`;
        const rg = new RenderGroup(rgKey, mesh, material, rgMatrices, spheres);
        out.push(rg);
      }
    }

    // If any entry in this bucket is tagged far-include, lift the kit's Skip
    // distance on the host InstanceGroup so all instances of this model stay
    // visible past Skip range. The flag is per-IG, so promoting one bucket
    // promotes every routed instance — consistent with the "promotes all
    // instances of the model" contract from the editor UI.
    const bucketHasFarInclude = entries.some((e) => (e.flags & FAR_FLAG_FORCE_INCLUDE) !== 0);

    // Bulk-add to ObjectManager — one Octree.insertArray call per tile rather
    // than one Octree.insert per RG. At ~5 RGs/tile and 25 tiles/ring,
    // this trims ~125 distribute() cascades per ring change.
    if (out.length > 0) {
      this.scene.objectManager.addObjects(out);
      // InstanceManager.registerGroup runs after octree insertion so the RG's
      // bounds are world-space when the octree captures them. Fresh-group LOD
      // kit application stays per-RG because the InstanceGroup may already be
      // populated by an earlier tile and only needs its kit installed once.
      for (const rg of out) {
        this.scene.instanceManager.registerGroup(rg);
        const ig = this.scene.instanceManager.getGroupForRenderGroup(rg);
        if (!ig) continue;
        if (ig.lodLevelCount === 0) {
          const kit = this.scene.modelLodKits.get(ref.model);
          if (kit) applyModelLodKitToGroup(this.scene, kit, ig, bucketHasFarInclude);
        } else if (bucketHasFarInclude) {
          // Kit was already installed by an earlier tile that hadn't seen any
          // flagged entries yet — undo the Skip cull so flagged instances in
          // later-loading tiles get the lift.
          ig.skipDistanceSq = null;
        }
      }
    }
    return out;
  }
}

interface LoadedTile {
  /** RenderGroups created for this tile — torn down when the tile evicts. */
  renderGroups: RenderGroup[];
  /** meshCache keys used by this tile's OBJECTS chunk, for ref-count decrement on eviction. */
  meshKeys: string[];
}

// ── Light chunk types + parser ────────────────────────────────────────────────

interface TileLightEntry {
  lightId: number;
  reference?: string;
  type: 0 | 1; // 0=point, 1=spot
  shadowMode: 0 | 1 | 2; // 0=none, 1=static, 2=dynamic
  position: [number, number, number];
  color: [number, number, number];
  outerRange: number;
  innerRange: number;
  direction: [number, number, number];
  dotInnerLimit: number;
  dotOuterLimit: number;
}

function parseLightsPayload(buf: Uint8Array): TileLightEntry[] {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const decoder = new TextDecoder();
  let off = 0;
  if (off + 2 > buf.byteLength) throw new Error('LIGHTS payload too short');
  const count = view.getUint16(off, true); off += 2;
  const entries: TileLightEntry[] = [];
  for (let i = 0; i < count; i++) {
    if (off + 2 > buf.byteLength) throw new Error('LIGHTS payload truncated (lightId)');
    const lightId = view.getUint16(off, true); off += 2;
    const r = readShortString(view, buf, off, decoder);
    off = r.next;
    const reference = r.value.length > 0 ? r.value : undefined;
    if (off + 2 + 52 > buf.byteLength) throw new Error('LIGHTS payload truncated (fixed body)');
    const type = view.getUint8(off) as 0 | 1; off += 1;
    const shadowModeRaw = view.getUint8(off); off += 1;
    const shadowMode = (shadowModeRaw <= 2 ? shadowModeRaw : 0) as 0 | 1 | 2;
    const px = view.getFloat32(off, true); off += 4;
    const py = view.getFloat32(off, true); off += 4;
    const pz = view.getFloat32(off, true); off += 4;
    const cr = view.getFloat32(off, true); off += 4;
    const cg = view.getFloat32(off, true); off += 4;
    const cb = view.getFloat32(off, true); off += 4;
    const outerRange = view.getFloat32(off, true); off += 4;
    const innerRange = view.getFloat32(off, true); off += 4;
    const dx = view.getFloat32(off, true); off += 4;
    const dy = view.getFloat32(off, true); off += 4;
    const dz = view.getFloat32(off, true); off += 4;
    const dotInner = view.getFloat32(off, true); off += 4;
    const dotOuter = view.getFloat32(off, true); off += 4;
    entries.push({
      lightId, reference, type, shadowMode,
      position: [px, py, pz],
      color: [cr, cg, cb],
      outerRange, innerRange,
      direction: [dx, dy, dz],
      dotInnerLimit: dotInner,
      dotOuterLimit: dotOuter,
    });
  }
  return entries;
}

function createLightFromEntry(entry: TileLightEntry): Light {
  const shadowModeStr = entry.shadowMode === 1 ? 'static' : entry.shadowMode === 2 ? 'dynamic' : 'none';
  if (entry.type === 1) {
    return new SpotLight({
      reference: entry.reference,
      position: entry.position,
      color: entry.color,
      innerRange: entry.innerRange,
      outerRange: entry.outerRange,
      direction: entry.direction,
      dotInnerLimit: entry.dotInnerLimit,
      dotOuterLimit: entry.dotOuterLimit,
      shadowMode: shadowModeStr,
    });
  }
  return new PointLight({
    reference: entry.reference,
    position: entry.position,
    color: entry.color,
    innerRange: entry.innerRange,
    outerRange: entry.outerRange,
    shadowMode: shadowModeStr,
  });
}

// ── Effect chunk types + parser ───────────────────────────────────────────────

interface TileEffectEntry {
  kind: 'liquid' | 'cloud' | 'flame';
  id: string;
  /** Empty array for flame entries (point primitives have no polygon). */
  polygon: number[][];
  // liquid fields
  fillY?: number;
  color?: [number, number, number, number];
  opacity?: number;
  amplitude?: number;
  waveSpeed?: number;
  waveDirection?: [number, number];
  shoreFadeDistance?: number;
  shoreDepthFade?: number;
  glintRoughness?: number;
  textureId?: string;
  // cloud fields
  name?: string;
  baseY?: number;
  topY?: number;
  edgeFeather?: number;
  density?: number;
  coverage?: number;
  noiseScale?: number;
  windDirX?: number;
  windDirZ?: number;
  windSpeed?: number;
  // flame fields
  position?: [number, number, number];
  scale?: number;
  flameIntensity?: number;
  flameColor?: [number, number, number];
}

function parseEffectsPayload(buf: Uint8Array): TileEffectEntry[] {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const decoder = new TextDecoder();
  let off = 0;
  if (off + 2 > buf.byteLength) throw new Error('EFFECTS payload too short');
  const count = view.getUint16(off, true); off += 2;
  const entries: TileEffectEntry[] = [];
  for (let i = 0; i < count; i++) {
    if (off + 1 > buf.byteLength) throw new Error('EFFECTS payload truncated (effectType)');
    const effectType = view.getUint8(off); off += 1;
    const idStr = readShortString(view, buf, off, decoder);
    off = idStr.next;
    const id = idStr.value;

    // Polygon prefix is only present for liquid (0) and cloud (1). Flame (2)
    // is a point primitive — skip the polygon read.
    let polygon: number[][] = [];
    if (effectType === 0 || effectType === 1) {
      if (off + 2 > buf.byteLength) throw new Error('EFFECTS payload truncated (vertexCount)');
      const vertexCount = view.getUint16(off, true); off += 2;
      if (off + vertexCount * 8 > buf.byteLength) throw new Error('EFFECTS payload truncated (polygon)');
      for (let v = 0; v < vertexCount; v++) {
        const x = view.getFloat32(off, true); off += 4;
        const z = view.getFloat32(off, true); off += 4;
        polygon.push([x, z]);
      }
    }

    if (effectType === 0) {
      // Liquid: 13 f32 fixed fields (52 bytes) + variable texture string
      if (off + 52 > buf.byteLength) throw new Error('EFFECTS liquid payload truncated');
      const fillY = view.getFloat32(off, true); off += 4;
      const lr = view.getFloat32(off, true); off += 4;
      const lg = view.getFloat32(off, true); off += 4;
      const lb = view.getFloat32(off, true); off += 4;
      const la = view.getFloat32(off, true); off += 4;
      const opacity = view.getFloat32(off, true); off += 4;
      const amplitude = view.getFloat32(off, true); off += 4;
      const waveSpeed = view.getFloat32(off, true); off += 4;
      const wdx = view.getFloat32(off, true); off += 4;
      const wdz = view.getFloat32(off, true); off += 4;
      const shoreFade = view.getFloat32(off, true); off += 4;
      const shoreDepth = view.getFloat32(off, true); off += 4;
      const glint = view.getFloat32(off, true); off += 4;
      const texStr = readShortString(view, buf, off, decoder);
      off = texStr.next;
      const textureId = texStr.value.length > 0 ? texStr.value : undefined;
      entries.push({ kind: 'liquid', id, polygon, fillY, color: [lr, lg, lb, la], opacity, amplitude, waveSpeed, waveDirection: [wdx, wdz], shoreFadeDistance: shoreFade, shoreDepthFade: shoreDepth, glintRoughness: glint, textureId });
    } else if (effectType === 1) {
      // Cloud: 9×f32 fixed body (36 bytes) then name string — matches encoder order in TileEffectChunk.kt
      if (off + 36 > buf.byteLength) throw new Error('EFFECTS cloud payload truncated');
      const baseY = view.getFloat32(off, true); off += 4;
      const topY = view.getFloat32(off, true); off += 4;
      const edgeFeather = view.getFloat32(off, true); off += 4;
      const density = view.getFloat32(off, true); off += 4;
      const coverage = view.getFloat32(off, true); off += 4;
      const noiseScale = view.getFloat32(off, true); off += 4;
      const windDirX = view.getFloat32(off, true); off += 4;
      const windDirZ = view.getFloat32(off, true); off += 4;
      const windSpeed = view.getFloat32(off, true); off += 4;
      const nameStr = readShortString(view, buf, off, decoder);
      off = nameStr.next;
      entries.push({ kind: 'cloud', id, polygon, name: nameStr.value, baseY, topY, edgeFeather, density, coverage, noiseScale, windDirX, windDirZ, windSpeed });
    } else if (effectType === 2) {
      // Flame: 8×f32 (32 bytes) — position(3) + scale + intensity + color(3)
      if (off + 32 > buf.byteLength) throw new Error('EFFECTS flame payload truncated');
      const px = view.getFloat32(off, true); off += 4;
      const py = view.getFloat32(off, true); off += 4;
      const pz = view.getFloat32(off, true); off += 4;
      const scale = view.getFloat32(off, true); off += 4;
      const flameIntensity = view.getFloat32(off, true); off += 4;
      const cr = view.getFloat32(off, true); off += 4;
      const cg = view.getFloat32(off, true); off += 4;
      const cb = view.getFloat32(off, true); off += 4;
      entries.push({
        kind: 'flame',
        id,
        polygon,
        position: [px, py, pz],
        scale,
        flameIntensity,
        flameColor: [cr, cg, cb],
      });
    } else {
      throw new Error(`EFFECTS unknown effectType: ${effectType}`);
    }
  }
  return entries;
}

function createLiquidSurface(scene: Scene, entry: TileEffectEntry): LiquidSurface | null {
  if (!entry.polygon || entry.polygon.length < 3) {
    console.warn(`Tiled liquid volume '${entry.id}' has no polygon (need ≥3 vertices)`);
    return null;
  }
  const polygon: PolygonVertex[] = entry.polygon.map(([x, z]) => [x, z] as const);
  const geom = tessellateLiquidPolygon(polygon, entry.fillY!);
  const surface = LiquidSurface.fromGeometry(
    scene.ctx,
    { positions: geom.positions, texCoords: geom.texCoords, indices: geom.indices, edgeDistance: geom.edgeDistance },
    {
      color: entry.color as [number, number, number, number],
      opacity: entry.opacity,
      amplitude: entry.amplitude,
      waveSpeed: entry.waveSpeed,
      waveDirection: entry.waveDirection as [number, number],
      texture: entry.textureId ? scene.textureCache.getTexture(entry.textureId) : undefined,
      shoreFadeDistance: entry.shoreFadeDistance,
      shoreDepthFade: entry.shoreDepthFade,
      glintRoughness: entry.glintRoughness,
    },
  );
  surface.setPosition(0, 0, 0);
  return surface;
}

interface TiledObjectEntry {
  name: string;
  modelRefId: number;
  position: number[];
  rotation: number[];
  scale: number;
  flags: number;
  /**
   * Cap on navmesh voxelization above this entry's Y. `Number.POSITIVE_INFINITY`
   * (encoded on disk as `Float.MAX_VALUE`) means "no cap" — manual placed
   * objects and tall climbable structures. Finite values come from
   * `BiomeScatterRule.maxNavGeometryHeight` (default 2.5m for scatter).
   */
  maxNavHeight: number;
}

interface TiledObjectGroup {
  models: TiledObjectModelRef[];
  entries: TiledObjectEntry[];
}

function readShortString(view: DataView, buf: Uint8Array, off: number, decoder: TextDecoder): { value: string; next: number } {
  if (off + 2 > buf.byteLength) throw new Error('Truncated reading u16 string length');
  const len = view.getUint16(off, true);
  const start = off + 2;
  if (start + len > buf.byteLength) throw new Error('Truncated reading string body');
  const value = decoder.decode(buf.subarray(start, start + len));
  return { value, next: start + len };
}

/**
 * Parse an OBJECTS chunk payload. Format (self-describing — no manifest coupling):
 *
 *   u16 modelCount
 *   per model:
 *     u16 modelIdLen,  <modelIdLen> UTF-8 bytes
 *     u16 meshNameLen, <meshNameLen> UTF-8 bytes
 *   u32 entryCount
 *   per entry:
 *     u16 nameLen, <nameLen> UTF-8 bytes
 *     u16 modelRefId    — index into THIS chunk's model table
 *     3 × f32 position
 *     4 × f32 rotation (quat x, y, z, w)
 *     f32 scale
 *     u32 flags
 */
/** v2 prelude marker; followed by `u16 version`. v1 chunks lack this. */
const OBJECTS_VERSION_SENTINEL = 0xfffe;

export function parseObjectsPayload(buf: Uint8Array): TiledObjectGroup {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  if (buf.byteLength < 2) throw new Error('OBJECTS payload too short');
  const decoder = new TextDecoder();
  let off = 0;
  // v1/v2 detection via leading-u16 sentinel. v1's first u16 is modelCount —
  // always far below 0xFFFE in practice — so 0xFFFE unambiguously indicates v2.
  let chunkVersion = 1;
  const firstU16 = view.getUint16(off, true);
  if (firstU16 === OBJECTS_VERSION_SENTINEL) {
    off += 2;
    chunkVersion = view.getUint16(off, true);
    off += 2;
  }
  const modelCount = view.getUint16(off, true); off += 2;
  const models: TiledObjectModelRef[] = [];
  for (let i = 0; i < modelCount; i++) {
    const m = readShortString(view, buf, off, decoder);
    off = m.next;
    const ms = readShortString(view, buf, off, decoder);
    off = ms.next;
    models.push({ model: m.value, meshName: ms.value });
  }
  if (off + 4 > buf.byteLength) throw new Error('OBJECTS payload truncated reading entryCount');
  const entryCount = view.getUint32(off, true); off += 4;
  const entries: TiledObjectEntry[] = [];
  // Body size: v1 = 38 bytes (no maxNavHeight); v2 = 42 bytes.
  const bodyBytes = chunkVersion >= 2 ? 42 : 38;
  for (let i = 0; i < entryCount; i++) {
    const n = readShortString(view, buf, off, decoder);
    off = n.next;
    if (off + bodyBytes > buf.byteLength) throw new Error('OBJECTS payload truncated reading entry body');
    const modelRefId = view.getUint16(off, true); off += 2;
    const px = view.getFloat32(off, true); off += 4;
    const py = view.getFloat32(off, true); off += 4;
    const pz = view.getFloat32(off, true); off += 4;
    const rx = view.getFloat32(off, true); off += 4;
    const ry = view.getFloat32(off, true); off += 4;
    const rz = view.getFloat32(off, true); off += 4;
    const rw = view.getFloat32(off, true); off += 4;
    const scale = view.getFloat32(off, true); off += 4;
    const flags = view.getUint32(off, true); off += 4;
    let maxNavHeight: number;
    if (chunkVersion >= 2) {
      const maxNavHeightRaw = view.getFloat32(off, true); off += 4;
      // Backend writes Float.MAX_VALUE (~3.4e38) as the "no cap" sentinel.
      maxNavHeight = maxNavHeightRaw > 1e30 ? Number.POSITIVE_INFINITY : maxNavHeightRaw;
    } else {
      // v1 chunks predate the field. Default to 2.5m — the publisher's
      // historical scatter cap. Manual objects (= no cap in reality) end up
      // mistakenly clipped, but they're rare in client-gen scenes; correct
      // behavior requires a republish to v2.
      maxNavHeight = 2.5;
    }
    entries.push({
      name: n.value,
      modelRefId,
      position: [px, py, pz],
      rotation: [rx, ry, rz, rw],
      scale,
      flags,
      maxNavHeight,
    });
  }
  return { models, entries };
}

/**
 * Entry-flags bit set by the publisher on placements tagged "far-include" in
 * the scene editor. Mirrors `FarObjectsBundle.ENTRY_FLAG_FORCE_INCLUDE`.
 */
const FAR_FLAG_FORCE_INCLUDE = 0x1;

/**
 * Lazy-build the kit's billboard mesh + impostor material from a temporary
 * full-mesh sample so we don't depend on a near InstanceGroup existing yet.
 * Idempotent: once cached on the kit, subsequent calls return immediately.
 * Returns false on missing model data; the caller should skip this bucket.
 */
function ensureFarBillboard(
  scene: Scene,
  kit: ModelLodKit,
  ref: TiledObjectModelRef,
  model: ParsedModel,
): boolean {
  if (!kit.billboardConfig) return false;
  if (kit.billboardMesh && kit.billboardMaterial) return true;
  const resolved = ref.meshName === '*' ? model.meshes[0] : model.meshByName.get(ref.meshName);
  const prim = resolved?.primitives[0];
  if (!prim) return false;
  const tempMesh = new GltfMesh(`${ref.model}_impostor_sample`, scene.ctx, scene, prim);
  const tempObj = scene.objectFactory.instantiateMesh(tempMesh);
  // Assign the model's material so the impostor render hits the textured
  // gbuffer branch. Without this, `albedo` defaults to vec4(1.0) and the baked
  // impostor is fully white — `ensureKitBillboard` caches billboardMaterial on
  // the kit and the near streamer never rebuilds it.
  if (prim.materialIndex !== undefined) {
    const mat = model.materials.get(prim.materialIndex);
    if (mat) tempObj.material = mat;
  }
  // Pull it out of the scene immediately — only used as the impostor source.
  scene.objectManager.removeObject(tempObj);
  const built = ensureKitBillboard(scene, kit, tempObj);
  tempMesh.destroy(scene.ctx.gl);
  return built;
}

interface FarObjectEntry {
  modelRefId: number;
  position: number[];
  rotation: number[];
  scale: number;
  flags: number;
}

interface FarObjectsBundle {
  models: TiledObjectModelRef[];
  entries: FarObjectEntry[];
}

const FAR_OBJECTS_MAGIC = 0x32424f46; // "FOB2" little-endian — matches FarObjectsBundle.kt

/**
 * Parse a far-objects bundle. Self-describing format:
 *
 *   u32 magic ("FOB2")
 *   u16 modelCount
 *   per model: u16 modelIdLen, bytes, u16 meshNameLen, bytes
 *   u32 entryCount
 *   per entry:
 *     u16 modelRefId      — index into the bundle's own model table
 *     3 × f32 position
 *     4 × f32 rotation (quat x, y, z, w)
 *     f32 scale
 *     u32 flags
 */
export function parseFarObjectsBundle(buf: Uint8Array): FarObjectsBundle {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  if (buf.byteLength < 6) throw new Error('Far-objects bundle too short');
  const magic = view.getUint32(0, true);
  if (magic !== FAR_OBJECTS_MAGIC) throw new Error('Far-objects bundle magic mismatch (expected FOB2)');
  const decoder = new TextDecoder();
  let off = 4;
  const modelCount = view.getUint16(off, true); off += 2;
  const models: TiledObjectModelRef[] = [];
  for (let i = 0; i < modelCount; i++) {
    const m = readShortString(view, buf, off, decoder);
    off = m.next;
    const ms = readShortString(view, buf, off, decoder);
    off = ms.next;
    models.push({ model: m.value, meshName: ms.value });
  }
  if (off + 4 > buf.byteLength) throw new Error('Far-objects bundle truncated reading entryCount');
  const entryCount = view.getUint32(off, true); off += 4;
  const entries: FarObjectEntry[] = [];
  const PER_ENTRY = 2 + 12 + 16 + 4 + 4;
  for (let i = 0; i < entryCount; i++) {
    if (off + PER_ENTRY > buf.byteLength) throw new Error('Far-objects bundle truncated');
    const modelRefId = view.getUint16(off, true); off += 2;
    const px = view.getFloat32(off, true); off += 4;
    const py = view.getFloat32(off, true); off += 4;
    const pz = view.getFloat32(off, true); off += 4;
    const rx = view.getFloat32(off, true); off += 4;
    const ry = view.getFloat32(off, true); off += 4;
    const rz = view.getFloat32(off, true); off += 4;
    const rw = view.getFloat32(off, true); off += 4;
    const scale = view.getFloat32(off, true); off += 4;
    const flags = view.getUint32(off, true); off += 4;
    entries.push({
      modelRefId,
      position: [px, py, pz],
      rotation: [rx, ry, rz, rw],
      scale,
      flags,
    });
  }
  return { models, entries };
}

interface TileCompositeEntry {
  instanceId: string;
  compositeId: string;
  position: [number, number, number];
  rotation: [number, number, number, number];
  scale: number;
  seed: number | undefined;
  state: Record<string, unknown> | undefined;
}

/**
 * Parse a COMPOSITES chunk payload. Format (matches Kotlin `TileCompositeChunk`):
 *
 *   u16 entryCount
 *   per entry:
 *     u16 instanceIdLen, <bytes>
 *     u16 compositeIdLen, <bytes>
 *     3×f32 position
 *     4×f32 rotation (quat x,y,z,w)
 *     f32 scale
 *     u8  hasSeed (0=absent, 1=present)
 *     i32 seed
 *     u16 stateLen, <bytes>      — JSON map; empty when no overrides
 */
function parseCompositesPayload(buf: Uint8Array): TileCompositeEntry[] {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const decoder = new TextDecoder();
  let off = 0;
  if (off + 2 > buf.byteLength) throw new Error('COMPOSITES payload too short');
  const count = view.getUint16(off, true);
  off += 2;
  const entries: TileCompositeEntry[] = [];
  for (let i = 0; i < count; i++) {
    const idStr = readShortString(view, buf, off, decoder);
    off = idStr.next;
    const compStr = readShortString(view, buf, off, decoder);
    off = compStr.next;
    if (off + 37 > buf.byteLength) {
      throw new Error('COMPOSITES payload truncated (fixed body)');
    }
    const px = view.getFloat32(off, true); off += 4;
    const py = view.getFloat32(off, true); off += 4;
    const pz = view.getFloat32(off, true); off += 4;
    const rx = view.getFloat32(off, true); off += 4;
    const ry = view.getFloat32(off, true); off += 4;
    const rz = view.getFloat32(off, true); off += 4;
    const rw = view.getFloat32(off, true); off += 4;
    const scale = view.getFloat32(off, true); off += 4;
    const hasSeed = view.getUint8(off); off += 1;
    const seedRaw = view.getInt32(off, true); off += 4;
    const stateStr = readShortString(view, buf, off, decoder);
    off = stateStr.next;
    let state: Record<string, unknown> | undefined;
    if (stateStr.value.length > 0) {
      try {
        state = JSON.parse(stateStr.value);
      } catch {
        state = undefined;
      }
    }
    entries.push({
      instanceId: idStr.value,
      compositeId: compStr.value,
      position: [px, py, pz],
      rotation: [rx, ry, rz, rw],
      scale,
      seed: hasSeed === 1 ? seedRaw : undefined,
      state,
    });
  }
  return entries;
}

export interface TileSoundEmitterEntry {
  id: string;
  clipId: string;
  position: [number, number, number];
  volume: number;
  spawnIntervalSec: number | undefined;
}

/**
 * Parse a SOUND_EMITTERS chunk payload. Format (matches Kotlin
 * `TileSoundEmitterChunk`):
 *
 *   u16 entryCount
 *   per entry:
 *     u16 idLen, <bytes>
 *     u16 clipIdLen, <bytes>
 *     3×f32 position
 *     f32  volume
 *     u8   hasInterval (0=looping bed, 1=spawn mode)
 *     f32  spawnIntervalSec
 */
export function parseSoundEmittersPayload(buf: Uint8Array): TileSoundEmitterEntry[] {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const decoder = new TextDecoder();
  let off = 0;
  if (off + 2 > buf.byteLength) throw new Error('SOUND_EMITTERS payload too short');
  const count = view.getUint16(off, true);
  off += 2;
  const entries: TileSoundEmitterEntry[] = [];
  for (let i = 0; i < count; i++) {
    const idStr = readShortString(view, buf, off, decoder);
    off = idStr.next;
    const clipStr = readShortString(view, buf, off, decoder);
    off = clipStr.next;
    if (off + 21 > buf.byteLength) {
      throw new Error('SOUND_EMITTERS payload truncated (fixed body)');
    }
    const px = view.getFloat32(off, true); off += 4;
    const py = view.getFloat32(off, true); off += 4;
    const pz = view.getFloat32(off, true); off += 4;
    const volume = view.getFloat32(off, true); off += 4;
    const hasInterval = view.getUint8(off); off += 1;
    const intervalRaw = view.getFloat32(off, true); off += 4;
    entries.push({
      id: idStr.value,
      clipId: clipStr.value,
      position: [px, py, pz],
      volume,
      spawnIntervalSec: hasInterval === 1 ? intervalRaw : undefined,
    });
  }
  return entries;
}
